import {
  Box,
  Flex,
  Heading,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type Account, type ChainFullId, formatPkh } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useGetSaplingAccount, useGetWallets } from "@umami/state";
import { useEffect } from "react";
import { QRCode } from "react-qrcode-logo";

import { ChevronRightIcon, CopyIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";
import { ModalBackButton } from "../BackButton";
import { ModalCloseButton } from "../CloseButton";
import { CopyButton } from "../CopyButton";
import { ChainIconWithName } from "../Token/ChainIconWithName";

import { light } from "@/styles/colors";

export const ReceiveModal = ({
  chainId,
  account,
  isSapling,
}: {
  chainId: ChainFullId;
  account: Account;
  isSapling?: boolean; // TODO: remove this prop once we have a better way to check if the chain is shielded
}) => {
  const color = useColor();
  const wallets = useGetWallets(account);
  const { goBack } = useDynamicModalContext();

  const saplingAccount = useGetSaplingAccount(account.address.pkh);
  const chain = wallets.find(item => item.chainId === chainId);

  const title = isSapling ? "Receive shielded assets on" : "Receive assets on";
  const addressText = isSapling ? "shielded address" : "address";
  const address =
    isSapling && saplingAccount ? saplingAccount.viewingKeyAddress : chain?.address.pkh;

  const isLongAddress = useBreakpointValue({ base: false, sm: true }) && !isSapling;

  useEffect(() => {
    hj.stateChange("receive_modal");
  }, []);

  if (!address) {
    throw new Error("The selected chain could not be found.");
  }

  return (
    <ModalContent data-testid="receive-modal">
      <ModalHeader>
        <VStack gap="12px">
          <Heading size="xs">{title}</Heading>
          <Box
            padding="5px 10px"
            borderRadius="md"
            _hover={{ backgroundColor: color("300") }}
            cursor="pointer"
            backgroundColor={color("100")}
            onClick={goBack}
          >
            <Flex alignItems="center" justifyContent="space-between">
              <ChainIconWithName chainId={chainId} />
              <Icon as={ChevronRightIcon} width="24px" height="24px" color={color("600")} />
            </Flex>
          </Box>
        </VStack>
        <ModalBackButton />
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <VStack gap="30px">
          <Box
            padding="5px"
            background={light.grey.white}
            borderColor={color("100")}
            borderRadius="md"
          >
            <QRCode fgColor={color("900", "100")} size={180} value={address} />
          </Box>
          <Box>
            <Heading color={color("900")} textAlign="center" size="lg">
              Your {addressText}
            </Heading>
            <Text color={color("500")} fontWeight="800" textAlign="center" size="sm">
              {isLongAddress ? address : formatPkh(address, isSapling)}
            </Text>
          </Box>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <CopyButton width="full" fontWeight="600" size="lg" value={address} variant="primary">
          <Icon as={CopyIcon} />
          <Text>Copy address</Text>
        </CopyButton>
      </ModalFooter>
    </ModalContent>
  );
};
